/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action, computed} from 'mobx';
import {observer, inject} from 'mobx-react';

import {parseQueryParams, getLangFromURLPathname, isTrue} from 'mad-utils/lib/react';
import {isDevelopment, isQA, isUAT} from 'env-var-helpers';

/**************************************** PROJECT MODULES *****************************************/
import {BaseStore} from '../base-store';

const showDevDefaults = isDevelopment && !isQA && !isUAT;

/********************************************* EXPORT *********************************************/
/**
 * Store values of environment variables (injected by CLI e.g. LOG_LEVEL)
 */
export class EnvVarStore extends BaseStore {
    public static Instance: EnvVarStore | null = null;

    // Environment variables injected by Webpack build in setup (defaults below)
    @observable.ref
    NODE_ENV: string = `production`;

    @observable.ref
    LOG_LEVEL: string = `info`;

    @observable.ref
    APP_VERSION: string = `?.?.?`;

    @observable.ref
    RELEASE_ENV: string = `production`;

    @observable.ref
    BUILD_NUMBER: string = `UNKNOWN`;

    @observable.ref
    AVOID_WEB: boolean = false;

    @observable.ref
    ALGOLIA_APPID: string = ``;

    @observable.ref
    ALGOLIA_APIKEY: string = ``;

    @observable.ref
    DEV_FULLNAME: string = ``;

    @observable.ref
    DEV_USERNAME: string = ``;

    @observable.ref
    DEV_PASSWORD: string = ``;

    @computed
    public get clientVersion(): string {
        return `${this.APP_VERSION}.${this.BUILD_NUMBER}-${this.RELEASE_ENV}`;
    }

    @computed
    get envVars() {
        return {
            NODE_ENV: this.NODE_ENV,
            LOG_LEVEL: this.LOG_LEVEL,
            APP_VERSION: this.APP_VERSION,
            RELEASE_ENV: this.RELEASE_ENV,
            BUILD_NUMBER: this.BUILD_NUMBER,
            AVOID_WEB: this.AVOID_WEB,
            ALGOLIA_APPID: this.ALGOLIA_APPID,
            ALGOLIA_APIKEY: this.ALGOLIA_APIKEY,
            DEV_FULLNAME: this.DEV_FULLNAME,
            DEV_USERNAME: this.DEV_USERNAME,
            DEV_PASSWORD: this.DEV_PASSWORD,
            clientVersion: this.clientVersion,
        };
    }

    /**
     * EnvVarStore singleton factory - true constructor
     * Grab query params from window
     * envVars must be passed up from their initial location in the entry file
     */
    public static new = (envVars?: Record<string, string>) => {
        if (!EnvVarStore.Instance) EnvVarStore.Instance = new EnvVarStore(envVars);
        return new EnvVarStore(envVars);
    };

    /**
     * Private internal constructor
     */
    private constructor(envVars?: Record<string, string>) {
        super();
        this._initializeEnvVars(envVars);
    }

    /**
     * Get environment vars of interest from envVars arg given to constructor
     */
    @action
    private _initializeEnvVars(envVars?: Record<string, string>) {
        this.NODE_ENV = envVars.NODE_ENV || process.env.NODE_ENV || this.NODE_ENV;
        this.LOG_LEVEL = envVars.LOG_LEVEL || process.env.LOG_LEVEL || this.LOG_LEVEL;
        this.APP_VERSION = envVars.APP_VERSION || process.env.APP_VERSION || this.APP_VERSION;
        this.RELEASE_ENV = envVars.RELEASE_ENV || process.env.RELEASE_ENV || this.RELEASE_ENV;
        this.BUILD_NUMBER = envVars.BUILD_NUMBER || process.env.BUILD_NUMBER || this.BUILD_NUMBER;

        const avoidWeb = envVars.AVOID_WEB || process.env.AVOID_WEB;
        this.AVOID_WEB = avoidWeb ? isTrue(avoidWeb) : this.AVOID_WEB;

        // Algolia key/auth injection
        this.ALGOLIA_APPID =
            envVars.ALGOLIA_APPID || process.env.ALGOLIA_APPID || this.ALGOLIA_APPID;
        this.ALGOLIA_APIKEY =
            envVars.ALGOLIA_APIKEY || process.env.ALGOLIA_APIKEY || this.ALGOLIA_APIKEY;

        this.DEV_FULLNAME = showDevDefaults
            ? envVars.DEV_FULLNAME || process.env.DEV_FULLNAME
            : this.DEV_FULLNAME;

        this.DEV_USERNAME = showDevDefaults
            ? envVars.DEV_USERNAME || process.env.DEV_USERNAME
            : this.DEV_USERNAME;

        this.DEV_PASSWORD = showDevDefaults
            ? envVars.DEV_PASSWORD || process.env.DEV_PASSWORD
            : this.DEV_PASSWORD;
    }
}
